import PropTypes from 'prop-types';
import { Grid, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  AccountCircle as AccountCircleIcon,
  Wc as WcIcon,
  MarkunreadMailbox as MarkunreadMailboxIcon,
  LocationCity as LocationCityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Cake as CakeIcon,
  Home as HomeIcon,
  Diversity1 as Diversity1Icon,
  School as SchoolIcon,
  Flag as FlagIcon,
  Key as KeyIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useGetGeoNameByGeonameIdAndLang from '../../hooks/getGeoName';

function ProfileShow({
  profile = {
    id: null,
    username: '',
    displayName: '',
    email: '',
    birthday: null,
    streetAddress: '',
    zipCode: '',
    city: null,
    country: null,
    gender: '',
    familyIncome: null,
    maritalStatus: '',
    educationalLevel: null,
  },
}) {
  const { t } = useTranslation();

  const boxColor = { backgroundColor: 'hsl(207 100% 90% / 1)', borderRadius: '10px' };

  return (
    <List data-testid="ProfileShow">
      <Grid container>
        <Grid item xs={12} sx={{ ...boxColor, mb: 2 }}>
          <Grid container p={2}>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="Username">
                <ListItemIcon>
                  <AccountCircleIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.username.label')} secondary={profile?.username || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="DisplayName">
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.displayName.label')} secondary={profile?.displayName || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ ...boxColor, mb: 2 }}>
          <Grid container p={2}>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="Email">
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.email.label')} secondary={profile?.email || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="Password">
                <ListItemIcon>
                  <KeyIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.password.label')} secondary="********" />
              </ListItem>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ ...boxColor, mb: 2 }}>
          <Grid container p={2}>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="Birthday">
                <ListItemIcon>
                  <CakeIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.birthday.label')} secondary={profile?.birthday ? dayjs(profile.birthday).format(t('dateFormat')) : t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="Gender">
                <ListItemIcon>
                  <WcIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.gender.label')} secondary={profile?.gender ? t(`profile.editForm.fields.gender.choices.${profile.gender}`) : t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ ...boxColor, mb: 2 }}>
          <Grid container p={2}>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="StreetAddress">
                <ListItemIcon>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.streetAddress.label')} secondary={profile?.streetAddress || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="ZipCode">
                <ListItemIcon>
                  <MarkunreadMailboxIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.zipCode.label')} secondary={profile?.zipCode || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="City">
                <ListItemIcon>
                  <LocationCityIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.city.label')} secondary={useGetGeoNameByGeonameIdAndLang(profile?.city, 'city') || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="Country">
                <ListItemIcon>
                  <FlagIcon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.country.label')} secondary={useGetGeoNameByGeonameIdAndLang(profile.country, 'country') || t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ ...boxColor, mb: 2 }}>
          <Grid container p={2}>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="FamilyIncome">
                <ListItemIcon>
                  <AccountBalanceWalletIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('profile.editForm.fields.familyIncome.label')}
                  secondary={profile?.familyIncome || profile?.familyIncome !== null ? t(`profile.editForm.fields.familyIncome.choices.${profile?.familyIncome}`) : t('profile.editForm.fields.noData')}
                />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="MaritalStatus">
                <ListItemIcon>
                  <Diversity1Icon />
                </ListItemIcon>
                <ListItemText primary={t('profile.editForm.fields.maritalStatus.label')} secondary={profile?.maritalStatus ? t(`profile.editForm.fields.maritalStatus.choices.${profile?.maritalStatus}`) : t('profile.editForm.fields.noData')} />
              </ListItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ListItem data-testid="EducationalLevel">
                <ListItemIcon>
                  <SchoolIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('profile.editForm.fields.educationalLevel.label')}
                  secondary={profile?.educationalLevel || profile?.educationalLevel !== null ? t(`profile.editForm.fields.educationalLevel.choices.${profile?.educationalLevel}`) : t('profile.editForm.fields.noData')}
                />
              </ListItem>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </List>
  );
}

ProfileShow.propTypes = {
  profile: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
    birthday: PropTypes.string,
    streetAddress: PropTypes.string,
    zipCode: PropTypes.string,
    city: PropTypes.number,
    country: PropTypes.number,
    gender: PropTypes.string,
    familyIncome: PropTypes.number,
    maritalStatus: PropTypes.string,
    educationalLevel: PropTypes.number,
  }),
};

export default ProfileShow;
